import { useEffect, useRef, useState } from "react";

/**
 * CategoryCombobox — searchable dropdown that also accepts free text.
 * Type to filter existing categories; click one to pick; or press Enter / blur to
 * commit the typed value (even if it doesn't match any existing option).
 *
 * Props:
 *  - value: current value (string)
 *  - onChange: (v) => void
 *  - options: string[] of existing categories
 *  - placeholder?: string
 *  - className?: string     (applied to the <input>)
 *  - testId?: string        (data-testid for the input, dropdown gets `${testId}-list`)
 *  - compact?: boolean      (smaller styling for use inside line-item rows)
 */
export default function CategoryCombobox({
    value, onChange, options = [], placeholder = "Type or pick…",
    className = "", testId, compact = false,
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState(value || "");
    const wrapRef = useRef(null);

    // Keep local text in sync with external value.
    useEffect(() => { setQ(value || ""); }, [value]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                // Commit whatever was typed.
                if (q !== (value || "")) onChange(q.trim() || "Uncategorized");
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open, q, value, onChange]);

    const norm = (q || "").trim().toLowerCase();
    const matches = options
        .filter((o) => o && o.toLowerCase().includes(norm))
        .slice(0, 40);
    const exact = options.some((o) => o.toLowerCase() === norm);
    const showCreateHint = q.trim() && !exact;

    const commit = (v) => {
        onChange((v || "").trim() || "Uncategorized");
        setQ(v || "");
        setOpen(false);
    };

    return (
        <div ref={wrapRef} className="relative">
            <input
                type="text"
                className={`hdb-input ${compact ? "text-xs py-1" : ""} ${className}`}
                value={q}
                placeholder={placeholder}
                onFocus={() => setOpen(true)}
                onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commit(q); }
                    if (e.key === "Escape") setOpen(false);
                }}
                data-testid={testId}
            />
            {open && (
                <div
                    className="absolute z-30 mt-1 bg-white border border-slate-200 rounded-md shadow-lg w-full max-h-56 overflow-y-auto"
                    data-testid={testId ? `${testId}-list` : undefined}>
                    {matches.length === 0 && !showCreateHint && (
                        <div className="px-3 py-2 text-xs text-slate-400">No categories yet</div>
                    )}
                    {matches.map((o) => (
                        <button
                            key={o} type="button" onMouseDown={(e) => e.preventDefault()}
                            onClick={() => commit(o)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0 ${
                                o.toLowerCase() === norm ? "bg-slate-50 font-semibold" : ""
                            }`}
                            data-testid={testId ? `${testId}-opt-${o}` : undefined}>
                            {o}
                        </button>
                    ))}
                    {showCreateHint && (
                        <button
                            type="button" onMouseDown={(e) => e.preventDefault()}
                            onClick={() => commit(q)}
                            className="w-full text-left px-3 py-1.5 text-sm border-t border-slate-100 hover:bg-emerald-50 text-emerald-800"
                            data-testid={testId ? `${testId}-create` : undefined}>
                            + Add new category "<span className="font-semibold">{q.trim()}</span>"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
